import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { useToast, apiErrorMessage } from '../context/ToastContext';
import {
  TOTAL_PAGES,
  TOTAL_JUZ,
  getJuzPageRange,
  getSurahForPage,
  getScoreClass,
  formatDate,
  daysSince,
  parseUtcDate,
  toApiDateTime,
  getHeatMapStyle,
  calculateMemorizationStrength,
  effectiveReviewInterval,
  quranPageImageUrl
} from '../utils/quranData';
import { MemorizedPage, PageReview, Profile, Surah } from '../types';
import { pageService, profileService } from '../services/dataService';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Message } from 'primereact/message';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

interface PageFormData {
  page_number: number;
  memorized_at: Date;
  reviewed_at: Date;
  score: number;
  notes: string;
}

interface BulkFormData {
  reviewed_at: Date;
  score: number;
  notes: string;
  updateScore: boolean;
  updateNotes: boolean;
}

interface PagesMap {
  [key: number]: MemorizedPage;
}

type PageRow = MemorizedPage & { strength: number };

function Pages(): React.ReactElement {
  const { profileId } = useParams<{ profileId: string }>();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { showSuccess, showError } = useToast();
  const [pages, setPages] = useState<PagesMap>({});
  const [loading, setLoading] = useState(true);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedPage, setSelectedPage] = useState<number | null>(null);
  const [pageHistory, setPageHistory] = useState<PageReview[]>([]);
  const [formData, setFormData] = useState<PageFormData>({
    page_number: 1,
    memorized_at: new Date(),
    reviewed_at: new Date(),
    score: 7,
    notes: ''
  });
  // Snapshot of the form as last loaded/saved, so we can detect unsaved edits
  const formBaseline = useRef<string>('');
  // Target page awaiting the unsaved-changes prompt, or null when none is pending
  const [pendingNav, setPendingNav] = useState<number | null>(null);
  // "?filter=review" (e.g. from the Dashboard quick action) opens the review tab
  const [activeTab, setActiveTab] = useState(searchParams.get('filter') === 'review' ? 1 : 0);
  const [profile, setProfile] = useState<Profile | null>(null);

  // Mushaf scan shown alongside the form; the choice sticks between sessions
  const [showPageImage, setShowPageImage] = useState(
    () => localStorage.getItem('showPageImage') === '1'
  );
  const [imageFailed, setImageFailed] = useState(false);

  const togglePageImage = (): void => {
    const next = !showPageImage;
    setShowPageImage(next);
    setImageFailed(false);
    localStorage.setItem('showPageImage', next ? '1' : '0');
  };

  // Heat map mode state
  const [heatMapMode, setHeatMapMode] = useState(true);

  // Multi-select mode state
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
  const [bulkDialogVisible, setBulkDialogVisible] = useState(false);
  const [bulkFormData, setBulkFormData] = useState<BulkFormData>({
    reviewed_at: new Date(),
    score: 7,
    notes: '',
    updateScore: false,
    updateNotes: false
  });
  const [pagesRangeText, setPagesRangeText] = useState('');
  const [saving, setSaving] = useState(false);

  const id = parseInt(profileId || '0', 10);
  const canEdit = profile?.permission !== 'read';
  const maxReviewDays = profile?.max_review_days || 7;

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  const loadData = async (): Promise<void> => {
    try {
      const profileResponse = await profileService.get(id);
      if (profileResponse.data.success) {
        setProfile(profileResponse.data.profile);
      }

      const pagesResponse = await pageService.getAll(id);
      if (pagesResponse.data.success) {
        const pagesMap: PagesMap = {};
        (pagesResponse.data.pages || []).forEach((page: MemorizedPage) => {
          pagesMap[page.page_number] = page;
        });
        setPages(pagesMap);
      }
    } catch (error) {
      showError(apiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const upsertLocalPages = (saved: MemorizedPage[]): void => {
    setPages(prev => {
      const next = { ...prev };
      saved.forEach(page => {
        next[page.page_number] = page;
      });
      return next;
    });
  };

  const removeLocalPages = (pageNumbers: number[]): void => {
    setPages(prev => {
      const next = { ...prev };
      pageNumbers.forEach(num => {
        delete next[num];
      });
      return next;
    });
  };

  // Comparable fingerprint of the editable fields, used to detect unsaved edits
  const formSnapshot = (data: PageFormData): string =>
    JSON.stringify({
      m: data.memorized_at?.getTime() ?? null,
      r: data.reviewed_at?.getTime() ?? null,
      s: data.score,
      n: data.notes
    });

  const savePage = async (): Promise<boolean> => {
    setSaving(true);
    try {
      const response = await pageService.save(id, {
        page_number: formData.page_number,
        memorized_at: toApiDateTime(formData.memorized_at),
        reviewed_at: toApiDateTime(formData.reviewed_at),
        score: formData.score,
        notes: formData.notes
      });
      if (response.data.success && response.data.page) {
        upsertLocalPages([response.data.page]);
      }
      // The saved state is now the clean baseline
      formBaseline.current = formSnapshot(formData);
      showSuccess();
      return true;
    } catch (error) {
      showError(apiErrorMessage(error));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    if (await savePage()) {
      setDialogVisible(false);
    }
  };

  const handleDelete = (): void => {
    const pageNumber = formData.page_number;
    confirmDialog({
      message: t('pages.confirmDeletePage', { page: pageNumber }),
      header: t('common.delete'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await pageService.delete(id, pageNumber);
          removeLocalPages([pageNumber]);
          setDialogVisible(false);
          showSuccess();
        } catch (error) {
          showError(apiErrorMessage(error));
        }
      }
    });
  };

  const parsePageRanges = (text: string): number[] => {
    const pageNumbers: Set<number> = new Set();
    const parts = text.split(',').map(p => p.trim()).filter(p => p);

    for (const part of parts) {
      if (part.includes('-')) {
        const [start, end] = part.split('-').map(n => parseInt(n.trim()));
        if (!isNaN(start) && !isNaN(end) && start >= 1 && end <= TOTAL_PAGES) {
          for (let i = Math.min(start, end); i <= Math.max(start, end); i++) {
            pageNumbers.add(i);
          }
        }
      } else {
        const num = parseInt(part);
        if (!isNaN(num) && num >= 1 && num <= TOTAL_PAGES) {
          pageNumbers.add(num);
        }
      }
    }
    return Array.from(pageNumbers).sort((a, b) => a - b);
  };

  const pagesToRangeText = (pagesSet: Set<number>): string => {
    const sorted = Array.from(pagesSet).sort((a, b) => a - b);
    if (sorted.length === 0) return '';

    const ranges: string[] = [];
    let rangeStart = sorted[0];
    let rangeEnd = sorted[0];

    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === rangeEnd + 1) {
        rangeEnd = sorted[i];
      } else {
        ranges.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}-${rangeEnd}`);
        rangeStart = sorted[i];
        rangeEnd = sorted[i];
      }
    }
    ranges.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}-${rangeEnd}`);
    return ranges.join(', ');
  };

  const handleRangeTextChange = (text: string): void => {
    setPagesRangeText(text);
    const parsed = parsePageRanges(text);
    setSelectedPages(new Set(parsed));
  };

  const togglePageSelection = (pageNumber: number): void => {
    const newSelection = new Set(selectedPages);
    if (newSelection.has(pageNumber)) {
      newSelection.delete(pageNumber);
    } else {
      newSelection.add(pageNumber);
    }
    setSelectedPages(newSelection);
    setPagesRangeText(pagesToRangeText(newSelection));
  };

  const loadHistory = async (pageNumber: number): Promise<void> => {
    setPageHistory([]);
    try {
      const response = await pageService.history(id, pageNumber);
      if (response.data.success) {
        setPageHistory(response.data.reviews || []);
      }
    } catch {
      // History is informational; ignore failures silently
    }
  };

  // Loads a page into the dialog form; used both when opening the dialog and
  // when stepping between pages from inside it.
  const goToPage = (pageNumber: number): void => {
    if (pageNumber < 1 || pageNumber > TOTAL_PAGES) return;

    setImageFailed(false);
    const existing = pages[pageNumber];
    const next: PageFormData = existing
      ? {
          page_number: pageNumber,
          memorized_at: parseUtcDate(existing.memorized_at),
          reviewed_at: parseUtcDate(existing.reviewed_at),
          score: existing.score,
          notes: existing.notes || ''
        }
      : {
          page_number: pageNumber,
          memorized_at: new Date(),
          reviewed_at: new Date(),
          score: 7,
          notes: ''
        };
    setFormData(next);
    formBaseline.current = formSnapshot(next);
    if (existing) {
      setSelectedPage(pageNumber);
      loadHistory(pageNumber);
    } else {
      setSelectedPage(null);
      setPageHistory([]);
    }
  };

  // True when the form has edits that differ from the last loaded/saved state
  const isDirty = canEdit && formSnapshot(formData) !== formBaseline.current;

  // Steps to another page, prompting first if the current page has unsaved edits
  const navigateToPage = (pageNumber: number): void => {
    if (pageNumber < 1 || pageNumber > TOTAL_PAGES) return;
    if (isDirty) {
      setPendingNav(pageNumber);
    } else {
      goToPage(pageNumber);
    }
  };

  const discardAndNavigate = (): void => {
    if (pendingNav !== null) goToPage(pendingNav);
    setPendingNav(null);
  };

  const saveAndNavigate = async (): Promise<void> => {
    const target = pendingNav;
    if (await savePage()) {
      if (target !== null) goToPage(target);
      setPendingNav(null);
    }
  };

  // Saves the current page then moves to the next one, keeping the dialog open
  const handleSaveAndNext = async (): Promise<void> => {
    if (await savePage()) {
      goToPage(formData.page_number + 1);
    }
  };

  const handlePageClick = (pageNumber: number): void => {
    if (multiSelectMode) {
      togglePageSelection(pageNumber);
      return;
    }
    goToPage(pageNumber);
    setDialogVisible(true);
  };

  const toggleMultiSelectMode = (): void => {
    if (multiSelectMode) {
      setSelectedPages(new Set());
      setPagesRangeText('');
    }
    setMultiSelectMode(!multiSelectMode);
  };

  const openBulkEditDialog = (): void => {
    if (selectedPages.size === 0) return;
    setBulkFormData({
      reviewed_at: new Date(),
      score: 7,
      notes: '',
      updateScore: false,
      updateNotes: false
    });
    setBulkDialogVisible(true);
  };

  const clearMultiSelect = (): void => {
    setMultiSelectMode(false);
    setSelectedPages(new Set());
    setPagesRangeText('');
  };

  const handleBulkSave = async (): Promise<void> => {
    if (selectedPages.size === 0) return;

    setSaving(true);
    try {
      // Only send fields the user opted to change; the backend keeps
      // existing values (or defaults for new pages) for omitted fields.
      const pagesData = Array.from(selectedPages).map(pageNumber => ({
        page_number: pageNumber,
        reviewed_at: toApiDateTime(bulkFormData.reviewed_at),
        ...(bulkFormData.updateScore ? { score: bulkFormData.score } : {}),
        ...(bulkFormData.updateNotes ? { notes: bulkFormData.notes } : {})
      }));

      const response = await pageService.bulkSave(id, pagesData);
      if (response.data.success && response.data.pages) {
        upsertLocalPages(response.data.pages);
      }
      setBulkDialogVisible(false);
      clearMultiSelect();
      showSuccess();
    } catch (error) {
      showError(apiErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDelete = (): void => {
    if (selectedPages.size === 0) return;
    const pageNumbers = Array.from(selectedPages);
    confirmDialog({
      message: t('pages.confirmDeleteSelected', { count: pageNumbers.length }),
      header: t('common.delete'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await pageService.bulkDelete(id, pageNumbers);
          removeLocalPages(pageNumbers);
          clearMultiSelect();
          showSuccess();
        } catch (error) {
          showError(apiErrorMessage(error));
        }
      }
    });
  };

  const clearSelection = (): void => {
    setSelectedPages(new Set());
    setPagesRangeText('');
  };

  const needsReview = (page: MemorizedPage | undefined): boolean => {
    if (!page) return false;
    const days = daysSince(page.reviewed_at);
    return days !== null && days > effectiveReviewInterval(page.score, maxReviewDays);
  };

  const getPageStatus = (pageNumber: number): string => {
    const page = pages[pageNumber];
    if (!page) return '';
    if (heatMapMode) return 'heat-map';
    if (needsReview(page)) return 'needs-review';
    return 'memorized';
  };

  const getPageStyle = (pageNumber: number): React.CSSProperties => {
    const page = pages[pageNumber];
    if (!page) return {};

    if (heatMapMode) {
      const days = daysSince(page.reviewed_at);
      return getHeatMapStyle(page.score, days, effectiveReviewInterval(page.score, maxReviewDays));
    }

    return {};
  };

  const getStrengthPercentage = (page: MemorizedPage): number => {
    const days = daysSince(page.reviewed_at);
    return Math.round(
      calculateMemorizationStrength(page.score, days, effectiveReviewInterval(page.score, maxReviewDays)) * 100
    );
  };

  // Precompute strength on rows so DataTable sorting works
  const memorizedList: PageRow[] = useMemo(
    () =>
      Object.values(pages)
        .map(page => ({ ...page, strength: getStrengthPercentage(page) }))
        .sort((a, b) => a.page_number - b.page_number),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pages, maxReviewDays]
  );
  const needsReviewList = memorizedList.filter(needsReview);

  const selectAllNeedsReview = (): void => {
    const needsReviewPages = new Set(needsReviewList.map(p => p.page_number));
    setSelectedPages(needsReviewPages);
    setPagesRangeText(pagesToRangeText(needsReviewPages));
  };

  const pageAriaLabel = (num: number): string => {
    const page = pages[num];
    if (!page) {
      return `${t('page.number')} ${num} | ${t('pages.notMemorized')}`;
    }
    const days = daysSince(page.reviewed_at);
    const parts = [
      `${t('page.number')} ${num}`,
      `${t('pages.strength')}: ${getStrengthPercentage(page)}%`,
      `${t('page.score')}: ${page.score}/10`
    ];
    if (days !== null) {
      parts.push(t('pages.daysAgo', { count: days }));
    }
    return parts.join(' | ');
  };

  const strengthBadge = (row: PageRow): React.ReactElement => {
    const days = daysSince(row.reviewed_at);
    const style = getHeatMapStyle(row.score, days, effectiveReviewInterval(row.score, maxReviewDays));
    return (
      <div
        style={{
          width: '48px',
          height: '24px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.75rem',
          fontWeight: 600,
          ...style
        }}
      >
        {row.strength}%
      </div>
    );
  };

  const pageNumberCell = (row: PageRow): React.ReactElement => {
    const surah = getSurahForPage(row.page_number);
    return (
      <div>
        <div className="font-bold">{row.page_number}</div>
        {surah && (
          <div className="text-sm text-color-secondary">
            {language === 'ar' ? surah.nameAr : surah.nameEn}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex align-items-center justify-content-center" style={{ height: '50vh' }}>
        <i className="pi pi-spin pi-spinner text-4xl"></i>
      </div>
    );
  }

  const surahForDialog: Surah | undefined = getSurahForPage(formData.page_number);

  // Page number and surah share the dialog title, so the body starts with the form
  const dialogHeader = (
    <div className="flex flex-column line-height-2">
      <span>
        {t('page.number')} {formData.page_number}
        {surahForDialog && ` · ${language === 'ar' ? surahForDialog.nameAr : surahForDialog.nameEn}`}
      </span>
      {surahForDialog && (
        <span className={`text-sm font-normal text-color-secondary ${language === 'ar' ? '' : 'arabic-text'}`}>
          {language === 'ar' ? surahForDialog.nameEn : surahForDialog.nameAr}
        </span>
      )}
    </div>
  );

  // Rendered in the Dialog's fixed footer region so it stays visible while the body scrolls
  const dialogFooter = (
    <div className="flex justify-content-between flex-wrap gap-2">
      {selectedPage && canEdit ? (
        <Button
          label={t('common.delete')}
          icon="pi pi-trash"
          className="p-button-danger p-button-text px-2"
          onClick={handleDelete}
        />
      ) : (
        <span />
      )}
      <div className="flex gap-2 flex-wrap">
        <Button
          label={canEdit ? t('common.cancel') : t('common.close')}
          className="p-button-text px-2"
          onClick={() => setDialogVisible(false)}
        />
        {canEdit && (
          <>
            <Button
              label={t('page.saveAndNext')}
              icon={`pi ${language === 'ar' ? 'pi-angle-double-left' : 'pi-angle-double-right'}`}
              iconPos="right"
              className="p-button-outlined px-2"
              onClick={handleSaveAndNext}
              loading={saving}
              disabled={formData.page_number >= TOTAL_PAGES}
            />
            <Button
              label={t('common.save')}
              onClick={handleSave}
              loading={saving}
              className="px-2"
            />
          </>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <ConfirmDialog />

      <div className="flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="text-2xl font-bold m-0">{profile?.name}</h1>
          <p className="text-color-secondary mt-1 mb-0">
            {Object.keys(pages).length} / {TOTAL_PAGES} {t('dashboard.pagesMemorized')}
            {profile?.owner_name && (
              <span className="ml-2">Â· {t('profile.sharedBy', { name: profile.owner_name })}</span>
            )}
          </p>
        </div>
      </div>

      {!canEdit && (
        <Message severity="info" text={t('pages.readOnlyBanner')} className="w-full mb-3" />
      )}

      <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
        <TabPanel header={t('nav.pages')}>
          <Card className="mb-4">
            <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h3 className="m-0">{t('nav.pages')}</h3>
              <div className="flex gap-2 align-items-center">
                <Button
                  label={heatMapMode ? t('pages.normalView') : t('pages.heatMap')}
                  icon={heatMapMode ? 'pi pi-th-large' : 'pi pi-chart-bar'}
                  className={`p-button-sm ${heatMapMode ? '' : 'p-button-outlined'}`}
                  onClick={() => setHeatMapMode(!heatMapMode)}
                />
                {canEdit && (
                  <Button
                    label={multiSelectMode ? t('common.cancel') : t('pages.multiSelect')}
                    icon={multiSelectMode ? 'pi pi-times' : 'pi pi-check-square'}
                    className={`p-button-sm ${multiSelectMode ? 'p-button-secondary' : 'p-button-outlined'}`}
                    onClick={toggleMultiSelectMode}
                  />
                )}
              </div>
            </div>

            {multiSelectMode && (
              <div className="mb-3 p-3 surface-100 border-round">
                <div className="flex flex-column gap-3">
                  <div className="flex align-items-center gap-2 flex-wrap">
                    <label htmlFor="pagesRange" className="font-medium white-space-nowrap">
                      {t('pages.pagesLabel')}
                    </label>
                    <InputText
                      id="pagesRange"
                      value={pagesRangeText}
                      onChange={(e) => handleRangeTextChange(e.target.value)}
                      placeholder={t('pages.rangeExample')}
                      className="flex-grow-1"
                      style={{ minWidth: '200px' }}
                    />
                  </div>
                  <div className="flex gap-2 flex-wrap align-items-center">
                    <Button
                      label={t('dashboard.needsReview')}
                      icon="pi pi-clock"
                      className="p-button-sm p-button-outlined p-button-warning"
                      onClick={selectAllNeedsReview}
                      disabled={needsReviewList.length === 0}
                    />
                    <Button
                      label={t('pages.clearSelection')}
                      icon="pi pi-times"
                      className="p-button-sm p-button-outlined p-button-secondary"
                      onClick={clearSelection}
                      disabled={selectedPages.size === 0}
                    />
                    <div className="flex-grow-1"></div>
                    <span className="text-color-secondary">
                      {t('pages.selectedCount', { count: selectedPages.size })}
                    </span>
                    <Button
                      label={t('pages.deleteSelected')}
                      icon="pi pi-trash"
                      className="p-button-sm p-button-outlined p-button-danger"
                      onClick={handleBulkDelete}
                      disabled={selectedPages.size === 0}
                    />
                    <Button
                      label={t('pages.editSelected')}
                      icon="pi pi-pencil"
                      className="p-button-sm"
                      onClick={openBulkEditDialog}
                      disabled={selectedPages.size === 0}
                    />
                  </div>
                </div>
              </div>
            )}

            {heatMapMode ? (
              <div className="flex gap-3 align-items-center text-sm mb-3 flex-wrap">
                <span className="text-color-secondary">{t('pages.memorizationStrength')}</span>
                <div className="heat-map-legend">
                  <span className="text-xs">{t('pages.weak')}</span>
                  <div className="heat-map-gradient"></div>
                  <span className="text-xs">{t('pages.strong')}</span>
                </div>
                <span className="flex align-items-center gap-1">
                  <span className="w-2rem h-2rem border-round" style={{ border: '1px solid #cbd5e1' }}></span>
                  {t('pages.notMemorized')}
                </span>
                {multiSelectMode && (
                  <span className="flex align-items-center gap-1">
                    <span className="w-1rem h-1rem border-round" style={{ background: '#3b82f6' }}></span>
                    {t('pages.selected')}
                  </span>
                )}
              </div>
            ) : (
              <div className="flex gap-3 align-items-center text-sm mb-3 flex-wrap">
                <span className="flex align-items-center gap-1">
                  <span className="w-1rem h-1rem border-round" style={{ background: '#10b981' }}></span>
                  {t('dashboard.pagesMemorized')}
                </span>
                <span className="flex align-items-center gap-1">
                  <span className="w-1rem h-1rem border-round" style={{ background: '#f59e0b' }}></span>
                  {t('dashboard.needsReview')}
                </span>
                {multiSelectMode && (
                  <span className="flex align-items-center gap-1">
                    <span className="w-1rem h-1rem border-round" style={{ background: '#3b82f6' }}></span>
                    {t('pages.selected')}
                  </span>
                )}
              </div>
            )}

            {Array.from({ length: TOTAL_JUZ }, (_, i) => i + 1).map(juz => {
              const { start, end } = getJuzPageRange(juz);
              return (
                <div key={juz} className="juz-section">
                  <div className="juz-header">
                    {t('pages.juz')} {juz}
                    <span className="juz-header-range">{start}â€“{end}</span>
                  </div>
                  <div className="page-grid" role="group" aria-label={`${t('pages.juz')} ${juz}`}>
                    {Array.from({ length: end - start + 1 }, (_, i) => start + i).map(num => (
                      <div
                        key={num}
                        role="button"
                        tabIndex={0}
                        aria-label={pageAriaLabel(num)}
                        aria-pressed={multiSelectMode ? selectedPages.has(num) : undefined}
                        className={`page-cell ${getPageStatus(num)} ${selectedPages.has(num) ? 'selected' : ''}`}
                        style={getPageStyle(num)}
                        onClick={() => handlePageClick(num)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handlePageClick(num);
                          }
                        }}
                        title={pageAriaLabel(num)}
                      >
                        {pages[num] ? (
                          <div className="page-cell-content">
                            <span className="page-cell-num">{num}</span>
                            <span className={`page-cell-score ${getScoreClass(pages[num].score)}`}>
                              {pages[num].score}
                            </span>
                          </div>
                        ) : (
                          num
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </Card>
        </TabPanel>

        <TabPanel header={`${t('dashboard.needsReview')} (${needsReviewList.length})`}>
          <Card>
            {needsReviewList.length === 0 ? (
              <div className="text-center p-4">
                <i className="pi pi-check-circle text-5xl text-primary mb-3"></i>
                <p className="text-color-secondary">{t('pages.allReviewed')}</p>
              </div>
            ) : (
              <DataTable value={needsReviewList} responsiveLayout="scroll">
                <Column field="page_number" header={t('page.number')} sortable body={pageNumberCell} />
                <Column field="reviewed_at" header={t('page.reviewedAt')} sortable
                  body={(row: PageRow) => (
                    <div>
                      <div>{formatDate(row.reviewed_at)}</div>
                      <div className="text-sm text-orange-500">
                        {t('pages.daysAgo', { count: daysSince(row.reviewed_at) ?? 0 })}
                      </div>
                    </div>
                  )}
                />
                <Column field="score" header={t('page.score')}
                  body={(row: PageRow) => (
                    <span className={`score-badge ${getScoreClass(row.score)}`}>
                      {row.score}
                    </span>
                  )}
                />
                <Column field="strength" header={t('pages.strength')} sortable body={strengthBadge} />
                <Column body={(row: PageRow) => (
                  <Button
                    icon="pi pi-refresh"
                    className="p-button-rounded p-button-text"
                    onClick={() => handlePageClick(row.page_number)}
                    tooltip={t('page.addReview')}
                    aria-label={t('page.addReview')}
                  />
                )} />
              </DataTable>
            )}
          </Card>
        </TabPanel>

        <TabPanel header={t('dashboard.pagesMemorized')}>
          <Card>
            <DataTable value={memorizedList} responsiveLayout="scroll" paginator rows={20}>
              <Column field="page_number" header={t('page.number')} sortable body={pageNumberCell} />
              <Column field="reviewed_at" header={t('page.reviewedAt')} sortable
                body={(row: PageRow) => formatDate(row.reviewed_at)}
              />
              <Column field="score" header={t('page.score')} sortable
                body={(row: PageRow) => (
                  <span className={`score-badge ${getScoreClass(row.score)}`}>
                    {row.score}
                  </span>
                )}
              />
              <Column field="strength" header={t('pages.strength')} sortable body={strengthBadge} />
              <Column body={(row: PageRow) => (
                <Button
                  icon="pi pi-pencil"
                  className="p-button-rounded p-button-text"
                  onClick={() => handlePageClick(row.page_number)}
                  aria-label={t('common.edit')}
                />
              )} />
            </DataTable>
          </Card>
        </TabPanel>
      </TabView>

      <Dialog
        header={dialogHeader}
        footer={dialogFooter}
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        style={{ width: '95vw', maxWidth: showPageImage ? '1000px' : '450px', maxHeight: '90vh' }}
        contentStyle={{ overflowY: 'auto' }}
      >
        <div className="grid">
          {/* Mushaf scan: stacked above the form on phones, side column on desktop */}
          {showPageImage && (
            <div className="col-12 lg:col-5">
              <div className="mushaf-pane">
                {imageFailed ? (
                  <div className="p-4 surface-100 border-round text-center text-color-secondary">
                    {t('page.imageUnavailable')}
                  </div>
                ) : (
                  <img
                    key={formData.page_number}
                    src={quranPageImageUrl(formData.page_number)}
                    alt={`${t('page.number')} ${formData.page_number}`}
                    className="mushaf-image"
                    onError={() => setImageFailed(true)}
                  />
                )}
              </div>
            </div>
          )}

          <div className={showPageImage ? 'col-12 lg:col-7' : 'col-12'}>
            <div className="flex align-items-center justify-content-between gap-2 mb-3">
              <Button
                icon={`pi ${language === 'ar' ? 'pi-chevron-right' : 'pi-chevron-left'}`}
                label={`${formData.page_number - 1}`}
                className="p-button-sm p-button-text"
                onClick={() => navigateToPage(formData.page_number - 1)}
                disabled={formData.page_number <= 1}
                tooltip={t('page.prevPage')}
                aria-label={t('page.prevPage')}
              />
              <div className="flex align-items-center gap-2">
                <span className="text-color-secondary text-sm">
                  {formData.page_number} / {TOTAL_PAGES}
                </span>
                <Button
                  icon={showPageImage ? 'pi pi-eye-slash' : 'pi pi-image'}
                  className="p-button-sm p-button-text p-button-rounded"
                  onClick={togglePageImage}
                  tooltip={showPageImage ? t('page.hideImage') : t('page.showImage')}
                  aria-label={showPageImage ? t('page.hideImage') : t('page.showImage')}
                  aria-pressed={showPageImage}
                />
              </div>
              <Button
                icon={`pi ${language === 'ar' ? 'pi-chevron-left' : 'pi-chevron-right'}`}
                iconPos="right"
                label={`${formData.page_number + 1}`}
                className="p-button-sm p-button-text"
                onClick={() => navigateToPage(formData.page_number + 1)}
                disabled={formData.page_number >= TOTAL_PAGES}
                tooltip={t('page.nextPage')}
                aria-label={t('page.nextPage')}
              />
            </div>

            <div className="formgrid grid">
              <div className="field col-12 sm:col-6 mb-3">
                <div className="flex align-items-center justify-content-between mb-2">
                  <label className="font-medium">{t('page.memorizedAt')}</label>
                  {canEdit && (
                    <Button
                      label={t('common.today')}
                      icon="pi pi-calendar-plus"
                      className="p-button-sm p-button-text py-0"
                      onClick={() => setFormData({ ...formData, memorized_at: new Date() })}
                    />
                  )}
                </div>
                <Calendar
                  value={formData.memorized_at}
                  onChange={(e) => setFormData({ ...formData, memorized_at: e.value as Date })}
                  className="w-full"
                  dateFormat="yy-mm-dd"
                  showIcon
                  showButtonBar
                  disabled={!canEdit}
                />
              </div>

              <div className="field col-12 sm:col-6 mb-3">
                <div className="flex align-items-center justify-content-between mb-2">
                  <label className="font-medium">{t('page.reviewedAt')}</label>
                  {canEdit && (
                    <Button
                      label={t('common.today')}
                      icon="pi pi-calendar-plus"
                      className="p-button-sm p-button-text py-0"
                      onClick={() => setFormData({ ...formData, reviewed_at: new Date() })}
                    />
                  )}
                </div>
                <Calendar
                  value={formData.reviewed_at}
                  onChange={(e) => setFormData({ ...formData, reviewed_at: e.value as Date })}
                  className="w-full"
                  dateFormat="yy-mm-dd"
                  showIcon
                  showButtonBar
                  disabled={!canEdit}
                />
              </div>
            </div>

            <div className="field mb-3">
              <label className="block mb-2 font-medium">{t('page.score')}</label>
              <div className="flex flex-wrap gap-2">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
                  <Button
                    key={score}
                    label={score.toString()}
                    className={`p-button-rounded ${formData.score === score ? '' : 'p-button-outlined'} ${
                      score <= 3 ? 'p-button-danger' : score <= 6 ? 'p-button-warning' : 'p-button-success'
                    }`}
                    style={{ width: '2.5rem', height: '2.5rem', padding: 0 }}
                    onClick={() => setFormData({ ...formData, score })}
                    disabled={!canEdit}
                    aria-pressed={formData.score === score}
                  />
                ))}
              </div>
            </div>

            <div className="field mb-4">
              <label className="block mb-2 font-medium">{t('page.notes')}</label>
              <InputTextarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full"
                disabled={!canEdit}
              />
            </div>

            {selectedPage && pageHistory.length > 0 && (
              <div className="field mb-4">
                <label className="block mb-2 font-medium">{t('pages.reviewHistory')}</label>
                <ul className="review-history m-0 p-0">
                  {pageHistory.map((review, idx) => (
                    <li key={idx} className="flex align-items-center gap-2 py-1">
                      <span className="text-color-secondary text-sm">{formatDate(review.reviewed_at)}</span>
                      {review.score !== null && (
                        <span className={`score-badge score-badge-sm ${getScoreClass(review.score)}`}>
                          {review.score}
                        </span>
                      )}
                      {review.notes && (
                        <span className="text-sm text-color-secondary review-history-notes">{review.notes}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </Dialog>

      <Dialog
        header={t('pages.unsavedTitle')}
        visible={pendingNav !== null}
        onHide={() => setPendingNav(null)}
        style={{ width: '90vw', maxWidth: '420px' }}
        footer={
          <div className="flex justify-content-end flex-wrap gap-2">
            <Button
              label={t('common.cancel')}
              className="p-button-text"
              onClick={() => setPendingNav(null)}
            />
            <Button
              label={t('pages.discardChanges')}
              className="p-button-text p-button-danger"
              onClick={discardAndNavigate}
            />
            <Button
              label={t('pages.saveAndContinue')}
              icon="pi pi-check"
              onClick={saveAndNavigate}
              loading={saving}
            />
          </div>
        }
      >
        <div className="flex align-items-start gap-3">
          <i className="pi pi-exclamation-triangle text-2xl text-orange-500 mt-1"></i>
          <span>{t('pages.unsavedChanges')}</span>
        </div>
      </Dialog>

      <Dialog
        header={t('pages.editPages', { count: selectedPages.size })}
        visible={bulkDialogVisible}
        onHide={() => setBulkDialogVisible(false)}
        style={{ width: '90vw', maxWidth: '450px' }}
      >
        <div className="mb-4 p-3 surface-100 border-round">
          <div className="text-sm text-color-secondary mb-1">{t('pages.selectedPages')}</div>
          <div className="font-medium" style={{ wordBreak: 'break-word' }}>
            {pagesRangeText || '-'}
          </div>
        </div>

        <div className="field mb-3">
          <label className="block mb-2 font-medium">{t('page.reviewedAt')}</label>
          <Calendar
            value={bulkFormData.reviewed_at}
            onChange={(e) => setBulkFormData({ ...bulkFormData, reviewed_at: e.value as Date })}
            className="w-full"
            dateFormat="yy-mm-dd"
            showIcon
          />
        </div>

        <div className="field mb-3">
          <div className="flex align-items-center gap-2 mb-2">
            <Checkbox
              inputId="bulkUpdateScore"
              checked={bulkFormData.updateScore}
              onChange={(e) => setBulkFormData({ ...bulkFormData, updateScore: !!e.checked })}
            />
            <label htmlFor="bulkUpdateScore" className="font-medium">{t('pages.updateScore')}</label>
          </div>
          {bulkFormData.updateScore && (
            <div className="flex flex-wrap gap-2">
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
                <Button
                  key={score}
                  label={score.toString()}
                  className={`p-button-rounded ${bulkFormData.score === score ? '' : 'p-button-outlined'} ${
                    score <= 3 ? 'p-button-danger' : score <= 6 ? 'p-button-warning' : 'p-button-success'
                  }`}
                  style={{ width: '2.5rem', height: '2.5rem', padding: 0 }}
                  onClick={() => setBulkFormData({ ...bulkFormData, score })}
                  aria-pressed={bulkFormData.score === score}
                />
              ))}
            </div>
          )}
        </div>

        <div className="field mb-2">
          <div className="flex align-items-center gap-2 mb-2">
            <Checkbox
              inputId="bulkUpdateNotes"
              checked={bulkFormData.updateNotes}
              onChange={(e) => setBulkFormData({ ...bulkFormData, updateNotes: !!e.checked })}
            />
            <label htmlFor="bulkUpdateNotes" className="font-medium">{t('pages.updateNotes')}</label>
          </div>
          {bulkFormData.updateNotes && (
            <InputTextarea
              value={bulkFormData.notes}
              onChange={(e) => setBulkFormData({ ...bulkFormData, notes: e.target.value })}
              rows={3}
              className="w-full"
            />
          )}
        </div>

        <p className="text-sm text-color-secondary mb-4">{t('pages.keepExistingHint')}</p>

        <div className="flex gap-2 justify-content-end">
          <Button
            label={t('common.cancel')}
            className="p-button-text"
            onClick={() => setBulkDialogVisible(false)}
          />
          <Button
            label={t('common.save')}
            icon="pi pi-check"
            onClick={handleBulkSave}
            loading={saving}
          />
        </div>
      </Dialog>
    </div>
  );
}

export default Pages;
